import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Role } from '@/types/roles';
import { useDeleteRoleWithReassignment } from '@/hooks/roles';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface RoleDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  role: Role;
  allRoles: Role[];
  userCount: number;
  onDeleteComplete: () => void;
}

export function RoleDeleteDialog({
  open,
  onClose,
  role,
  allRoles,
  userCount,
  onDeleteComplete,
}: RoleDeleteDialogProps) {
  const [replacementRoleId, setReplacementRoleId] = useState<string>('');
  const deleteRole = useDeleteRoleWithReassignment();

  // Reset selection when dialog opens or role changes to prevent stale state
  useEffect(() => {
    if (open) {
      setReplacementRoleId('');
    }
  }, [open, role.id]);

  const availableRoles = allRoles.filter(r => r.id !== role.id);
  const hasUsersAssigned = userCount > 0;

  const handleDelete = async () => {
    try {
      // Use atomic delete with reassignment RPC
      await deleteRole.mutateAsync({
        roleId: role.id,
        replacementRoleId: hasUsersAssigned ? replacementRoleId : undefined
      });
      onDeleteComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Failed to delete role: ' + message);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Role: {role.name}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. The role will be permanently deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {hasUsersAssigned && (
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-destructive/10 p-4 text-sm">
              <p className="font-medium text-destructive">
                {userCount} user{userCount === 1 ? ' is' : 's are'} currently assigned to this role.
              </p>
              <p className="text-muted-foreground mt-1">
                Please select a replacement role for these users before deleting.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="replacement-role">Replacement Role</Label>
              <Select
                value={replacementRoleId}
                onValueChange={setReplacementRoleId}
              >
                <SelectTrigger id="replacement-role">
                  <SelectValue placeholder="Select a replacement role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteRole.isPending}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDelete}
            disabled={deleteRole.isPending || (hasUsersAssigned && !replacementRoleId)}
          >
            {deleteRole.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Role
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
